import { useContext, useEffect, useReducer, useState } from "react";

import { makeStyles } from "@material-ui/core";

import MainContainer from "../../components/MainContainer";

import { toast } from "react-toastify";
import { KTSVG } from '../../../_metronic/helpers';
import ConfirmationModal from "../../components/ConfirmationModal";
import CategoryAIModal from "../../components/CategoryAIModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

import { Switch } from "@mui/material";
import socket from "../../hooks/useSocket";
import { Button } from "@material-ui/core";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BlockIcon from "@material-ui/icons/Block";

const reducer = (state, action) => {
  if (action.type === "LOAD_CATEGORIES") {
    const categories = action.payload;
    const newCategories = [];

    categories.forEach((category) => {
      const categoryIndex = state.findIndex((q) => q.id === category.id);
      if (categoryIndex !== -1) {
        state[categoryIndex] = category;
      } else {
        newCategories.push(category);
      }
    });

    return [...state, ...newCategories];
  }

  if (action.type === "UPDATE_CATEGORIES") {
    const category = action.payload;
    const categoryIndex = state.findIndex((q) => q.id === category.id);
    if (categoryIndex !== -1) {
      state[categoryIndex] = category;
      return [...state];
    } else {
      return [category, ...state];
    }
  }

  if (action.type === "DELETE_CATEGORIES") {
    const categoryId = action.payload;

    const categoryIndex = state.findIndex((q) => q.id === categoryId);
    if (categoryIndex !== -1) {
      state.splice(categoryIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  modalStyle: {
    minWidth: '395px',
    margin: '20px 10px!important',
    padding: '10px',
    "@media (max-width: 740px)": {
      padding: '0px !important',
      marginTop: '65px'
    }
  }
}));

const Categories = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [categories, dispatch] = useReducer(reducer, []);
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [deletingCategories, setDeletingCategories] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const { user } = useContext(AuthContext);

  const adminId = user.adminId;

  const [getUsers, setGetUsers] = useState([]);
  const [getCreatedBy, setSelectedUserFilter] = useState("");


  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, getCreatedBy]);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/usersList/", {
            signal: abortController.signal,
          });
          if (!isMounted) return;
          setGetUsers(data)
        } catch (err) {
          if (!isMounted || err.name == 'AbortError') return;
          console.error({err});
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);

    return () => {
      isMounted = false;
      abortController.abort();
      clearTimeout(delayDebounceFn);
    };

  }, []);

  useEffect(() => {
    setLoading(true);
    let isMounted = true;
    const abortController = new AbortController();

    const delayDebounceFn = setTimeout(() => {
      const fetchCategories = async () => {
        try {
          const { data } = await api.get("/categories/", {
            params: { searchParam, pageNumber, getCreatedBy },
            signal: abortController.signal,
          });
          if (!isMounted) return;
          dispatch({ type: "LOAD_CATEGORIES", payload: data.categories });
          setHasMore(!!data?.hasMore);
          setLoading(false);
        } catch (err) {
          if (!isMounted || err.name == 'AbortError') return;
          console.error({err});
          toastError(err);
        }
      };
      fetchCategories();
    }, 500);

    return () => {
      isMounted = false;
      abortController.abort();
      clearTimeout(delayDebounceFn);
    };
  }, [searchParam, pageNumber, getCreatedBy]);

  useEffect(() => {
    const categoriesEventName = `categories-${adminId}`;
    const categoriesEventFunction = async (data) => {

      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CATEGORIES", payload: data.category });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_CATEGORIES",
          payload: +data.categoryId,
        });
      }
    };
    socket.on(categoriesEventName, categoriesEventFunction);

    return () => {
      socket.off(categoriesEventName, categoriesEventFunction);
    };
  }, []);

  const handleChange = async (event) => {
    if (event.target.value === "") {
      setSelectedUserFilter("");
    } else {
      setSelectedUserFilter(event.target.value);
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenCategoryModal = () => {
    setSelectedCategories(null);
    setCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setSelectedCategories(null);
    setCategoryModalOpen(false);
  };

  const handleEditCategories = (category) => {
    setSelectedCategories(category);
    setCategoryModalOpen(true);
  };

  const handleDisableCategories = async (categoryData) => {
    try {
      categoryData = { ...categoryData, status: !categoryData.status };
      await api.put(`/categories/${categoryData.id}`, categoryData);
      toast.success(i18n.t("categoryAIModal.success"));
    } catch (err) {
      console.error({err});
      toastError(err);
    }
    setDeletingCategories(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleDeleteCategories = async (categoryId) => {
      try {
        await api.delete(`/categories/${categoryId}`);
        toast.success(i18n.t("categoryAI.toasts.deleted"));
      } catch (err) {
        console.error({err});
        toastError(err);
      }
      setDeletingCategories(null);
      setSearchParam("");
      setPageNumber(1);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCategories &&
          `${i18n.t("categoryAI.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCategories(deletingCategories.id)}
      >
        {i18n.t("categoryAI.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <CategoryAIModal
        open={categoryModalOpen}
        onClose={handleCloseCategoryModal}
        aria-labelledby="form-dialog-title"
        categoryId={selectedCategories && selectedCategories.id}
      ></CategoryAIModal>
      <div class='row gy-5 gx-xl-8'
        className={classes.modalStyle}
        onScroll={handleScroll}>
        <div className='col-xl-15'>
          <div className='card card-xxl-stretch-5 mb-15 mb-xl-8'>
            {/* begin::Header */}
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>{i18n.t("categoryAI.title")}</span>
                {/*<span className='text-muted mt-1 fw-bold fs-7'>Over 500 members</span>*/}
              </h3>
              <div
                className='card-toolbar '
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                data-bs-trigger='hover'
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenCategoryModal}
                >
                  <KTSVG path='media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  {i18n.t("categoryAI.buttons.add")}
                </Button>
              </div>
            </div>
            {/* end::Header */}
            {/* begin::Body */}
            <div className='card-body py-3'>
              {/* begin::Table container */}
              <div className='table-responsive'>
                {/* begin::Table */}
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  {/* begin::Table head */}
                  <thead>
                    <tr className='fw-bolder text-muted'>
                      <th className='min-w-150px'>{i18n.t("categoryAI.table.name")}</th>
                      <th className='min-w-150px'>{i18n.t("categoryAI.table.description")}</th>
                      <th className='min-w-120px'>{i18n.t("categoryAI.table.status")}</th>
                      <th className='min-w-120px'>{i18n.t("categoryAI.table.updatedBy")}</th>
                      <th className='min-w-100px text-end'>{i18n.t("categoryAI.table.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <>
                      {categories.map((category) => (
                        <>
                          <tr >
                            <td>
                              <div>
                                <div className='d-flex justify-content-start flex-column'>
                                    {category.name}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className='d-flex justify-content-start flex-column'>
                                    {category.description}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className='d-flex justify-content-start flex-column'>
                                  <Switch
                                    checked={category.status}
                                    onChange={() => handleDisableCategories(category)}
                                    color="primary"
                                    name={`status${category.status}`}
                                    key={`status${category.status}`}
                                    inputProps={{ 'aria-label': 'handle status' }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className='d-flex justify-content-start flex-column'>
                                  <a href='#' className='text-dark fw-bolder text-hover-primary fs-6'>
                                    {category?.['User.name']}
                                  </a>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className='d-flex justify-content-end flex-shrink-0'>

                                <button
                                  title={i18n.t("buttons.edit")}
                                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                  onClick={() => handleEditCategories(category)}
                                >
                                  <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                                </button>

                                {/* <button
                                  title={i18n.t("buttons.delete")}
                                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                  onClick={(e) => {
                                    setConfirmModalOpen(true);
                                    setDeletingCategories(category);
                                  }}
                                >
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen027.svg'
                                    className='svg-icon-3'
                                  />
                                </button>              */}
                              </div>
                            </td>
                          </tr>
                        </>
                      ))}

                    </>
                  </tbody>
                  {/* end::Table body */}
                </table>
                {/* end::Table */}
              </div>
              {/* end::Table container */}
            </div>
            {/* begin::Body */}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Categories;
